import { Form, Formik } from "formik";
import { useState } from "react";
import { Modal } from "react-bootstrap";
import { Button, ThemeProviderV2, DatePickerRange, Field, FieldLabel} from '@blueprint/ui';
import "react-datepicker/dist/react-datepicker.css";
import { useModal } from "react-modal-hook";

import { Loader } from "../../../common/components/core";
import { InputField, SelectField } from "../../../common/components/form";
import { SelectOption } from "../../../common/components/form/Select";
import { withIdInputField } from "../../../common/components/form/HOC/withIdInputField";

type FilterHandler = {
    show: () => void;
    hide: () => void;
};

export type FilterInvoiceListFullFormValues = {
    claimId: string;
    invoiceId: string;
    externalBusinessId: string;
    mainContractorName: string;
    projectId: string;
    dateFrom: Date | null;
    dateTo: Date | null;
};

const IdInputField = withIdInputField(InputField);

const useFilterInvoiceListFullHandler = (
    projects: SelectOption[],
    mainContractors: SelectOption[],
    currentFilter: FilterInvoiceListFullFormValues,
    onSubmit: (value: FilterInvoiceListFullFormValues) => void,
): FilterHandler => {
    const [dateRange, setDateRange] = useState<(Date | null)[]>([
        currentFilter.dateFrom,
        currentFilter.dateTo,
    ]);
    const setDates = (date: [Date | null, Date | null]) => {
        setDateRange(date);
    };

    const onResetHandler = () => {
        onSubmit({
            claimId: "",
            invoiceId: "",
            externalBusinessId: "",
            mainContractorName: "",
            projectId: "",
            dateFrom: null,
            dateTo: null,
        });
        setDates([null, null]);
        hide();
    };

    const [show, hide] = useModal(() => {
        return (
            <ThemeProviderV2>
            <Modal show backdrop centered onHide={hide} id="filter-modal">
                <Formik
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={(value: FilterInvoiceListFullFormValues) => {
                        onSubmit({
                            ...value,
                            dateFrom: dateRange[0],
                            dateTo: dateRange[1],
                        });
                        hide();
                    }}
                    initialValues={{
                        ...currentFilter,
                    }}
                >
                    {({ handleSubmit, isSubmitting, values }) => {
                        return (
                            <ThemeProviderV2>
                            <Loader isLoading={isSubmitting}>
                                <Modal.Header>
                                    <h2 className="mb-0">
                                        Filter
                                    </h2>
                                </Modal.Header>
                                <Modal.Body>
                                    <Form
                                        autoComplete="off"
                                        noValidate
                                        onSubmit={handleSubmit}
                                    >
                                        <SelectField
                                            name="mainContractorName"
                                            label="Service Provider Name"
                                            menuPosition="fixed"
                                            options={mainContractors}
                                        />
                                        <SelectField
                                            name="projectId"
                                            label="Project Name"
                                            menuPosition="fixed"
                                            options={projects}
                                        />
                                        <IdInputField
                                            type="text"
                                            name="claimId"
                                            label="Claim ID"
                                            maxLength={8}
                                        />
                                        <IdInputField
                                            type="text"
                                            name="invoiceId"
                                            label="Invoice ID"
                                            maxLength={8}
                                        />
                                        <IdInputField
                                            type="text"
                                            name="externalBusinessId"
                                            label="Payment Order ID"
                                            maxLength={16}
                                        />
                                        <div>
                                            {/* Timestamp new date picker */}

                                            <Field>
                                                <FieldLabel marginBottom='6px'>Timestamp</FieldLabel>
                                                <DatePickerRange
                                                    singleLine={true}
                                                    handleFromDateChange={(value: any)=>{
                                                        setDates([new Date(new Date(value).toLocaleDateString()), dateRange[1]])
                                                    }}
                                                    handleToDateChange={(value: any)=>{
                                                        setDates([dateRange[0], new Date(new Date(value).toLocaleDateString())])
                                                    }}
                                                />
                                            </Field>
                                        </div>
                                        <Modal.Footer className="justify-content-between px-0 pb-0">
                                            <Button
                                                variant="light.tertiary"
                                                type="reset"
                                                onClick={onResetHandler}
                                            >
                                                Reset
                                            </Button>
                                            <Button
                                                variant="light.primary"
                                                type="submit"
                                            >
                                                Apply
                                            </Button>
                                        </Modal.Footer>
                                    </Form>
                                </Modal.Body>
                            </Loader>
                            </ThemeProviderV2>
                        );
                    }}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [currentFilter, dateRange, projects, mainContractors]);

    return {
        show,
        hide,
    };
};

export default useFilterInvoiceListFullHandler;
